import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { operationsApi } from '@/services/api';
import type { ChecklistListItem } from '@/types/operations';
import { formatDate, formatDateTime } from '@/utils/formatters';
import { ChecklistStatusBadge } from './presentation';
import { getApiErrorMessage } from './helpers';
import DataTable, { type ColumnDef } from '@/components/utils/DataTable';
import { useTableState } from '@/hooks/useTableState';
import shared from '@/components/utils/shared.module.css';

const PAGE_SIZE = 20;

const OperationsHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ChecklistListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    currentPage,
    totalPages,
    totalCount,
    sortField,
    sortDir,
    ordering,
    onSort,
    onPageChange,
    setTotalCount,
    pageSize,
  } = useTableState({ defaultSortField: 'checklist_date', defaultSortDir: 'desc', pageSize: PAGE_SIZE });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await operationsApi.list({ page: currentPage, page_size: PAGE_SIZE, ordering });
      setItems(response.results);
      setTotalCount(response.count);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load checklist history.'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, ordering, setTotalCount]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo((): ColumnDef<ChecklistListItem>[] => [
    {
      key: 'checklist_date',
      header: 'Date',
      render: (row) => formatDate(row.checklist_date),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <ChecklistStatusBadge status={row.status} />,
    },
    {
      key: 'passed',
      header: 'Passed',
      align: 'right',
      render: (row) => row.step_counts.PASSED,
    },
    {
      key: 'failed',
      header: 'Failed',
      align: 'right',
      render: (row) => row.step_counts.FAILED,
    },
    {
      key: 'pending',
      header: 'Pending',
      align: 'right',
      render: (row) => row.step_counts.PENDING,
    },
    {
      key: 'exempted',
      header: 'Exempted',
      align: 'right',
      render: (row) => row.step_counts.EXEMPTED,
    },
    {
      key: 'completed_ts',
      header: 'Completed',
      render: (row) => (row.completed_ts ? formatDateTime(row.completed_ts) : '—'),
    },
  ], []);

  return (
    <DataTable
      title="Checklist History"
      subtitle="Past operational checklists, newest first."
      data={items}
      columns={columns}
      rowKey={(row) => row.id}
      totalCount={totalCount}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      pageSize={pageSize}
      sortField={sortField}
      sortDir={sortDir}
      onSort={onSort}
      loading={loading}
      error={error}
      hideSearch
      emptyMessage="No past checklists yet."
      onRowClick={(row) => navigate(`/operations-checklist/${row.id}`)}
      headerActions={
        <Link to="/operations-checklist" className={shared['btn-secondary']}>
          <ArrowLeft size={15} /> Current checklist
        </Link>
      }
    />
  );
};

export default OperationsHistoryPage;
